import { useCallback, useState } from 'react';
import { AxiosError } from 'axios';
import { IOption } from 'components/elements/CustomSelect';
import toastService from 'components/elements/Toastify';
import { IErrorResponse } from 'types';
import { CITIES_FIELDS_NAME, SORT_DIR } from 'utils/enums';
import { getCityOptionTitle, getErrorMessage } from 'utils/helpers';
import { CitiesService } from 'utils/services';

interface UseCityAutocompleteOptions {
	deviceFP: string;
	limit?: number;
}

interface UseCityAutocompleteResult {
	cityOptions: IOption[];
	handleLoadMoreCities: () => Promise<void>;
	handleSearchCities: (searchValue: string) => Promise<void>;
	hasMoreCityOptions: boolean;
	isCityOptionsLoading: boolean;
	resetCityAutocomplete: () => void;
}

export const useCityAutocomplete = ({
	deviceFP,
	limit = 15,
}: UseCityAutocompleteOptions): UseCityAutocompleteResult => {
	const [isCityOptionsLoading, setIsCityOptionsLoading] = useState(false);
	const [citySearchState, setCitySearchState] = useState({
		query: '',
		total: 0,
	});
	const [cityOptions, setCityOptions] = useState<IOption[]>([]);

	const resetCityAutocomplete = useCallback(() => {
		setCitySearchState({
			query: '',
			total: 0,
		});
		setCityOptions([]);
	}, []);

	const handleSearchCities = useCallback(async (searchValue: string): Promise<void> => {
		const normalizedSearchValue = searchValue.trim();

		if (!normalizedSearchValue) {
			resetCityAutocomplete();
			return;
		}

		setIsCityOptionsLoading(true);

		try {
			const response = await CitiesService.getCities({
				offset: 0,
				limit,
				orderBy: [
					{
						field: CITIES_FIELDS_NAME.NAME,
						direction: SORT_DIR.DSC,
					},
				],
				searchBy: {
					name: normalizedSearchValue,
				},
				deviceFP,
			});

			const cities = Array.isArray(response.data) ? response.data : response.data.results || [];
			const total = Array.isArray(response.data) ? cities.length : response.data.total || cities.length;

			setCitySearchState({
				query: normalizedSearchValue,
				total,
			});
			setCityOptions(
				cities.map(({ id, name }) => ({
					title: getCityOptionTitle(id, name),
					value: `${id}`,
				}))
			);
		} catch (error) {
			toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
		} finally {
			setIsCityOptionsLoading(false);
		}
	}, [deviceFP, limit, resetCityAutocomplete]);

	const handleLoadMoreCities = useCallback(async (): Promise<void> => {
		if (!citySearchState.query || isCityOptionsLoading || cityOptions.length >= citySearchState.total) {
			return;
		}

		setIsCityOptionsLoading(true);

		try {
			const response = await CitiesService.getCities({
				offset: cityOptions.length,
				limit,
				orderBy: [
					{
						field: CITIES_FIELDS_NAME.NAME,
						direction: SORT_DIR.DSC,
					},
				],
				searchBy: {
					name: citySearchState.query,
				},
				deviceFP,
			});

			const cities = Array.isArray(response.data) ? response.data : response.data.results || [];
			const nextOptions = cities.map(({ id, name }) => ({
				title: getCityOptionTitle(id, name),
				value: `${id}`,
			}));

			setCityOptions((prevState) => {
				const mergedOptions = [...prevState];

				nextOptions.forEach((option) => {
					if (!mergedOptions.some((existingOption) => existingOption.value === option.value)) {
						mergedOptions.push(option);
					}
				});

				return mergedOptions;
			});
		} catch (error) {
			toastService.error(getErrorMessage(error as AxiosError<IErrorResponse>));
		} finally {
			setIsCityOptionsLoading(false);
		}
	}, [cityOptions.length, citySearchState, deviceFP, isCityOptionsLoading, limit]);

	return {
		cityOptions,
		handleLoadMoreCities,
		handleSearchCities,
		hasMoreCityOptions: cityOptions.length < citySearchState.total,
		isCityOptionsLoading,
		resetCityAutocomplete,
	};
};
